import { Eye, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import type { QuestionsApi, TryOutcome } from '../api/questions'
import { useOpenInSimulator } from './openInSimulator'

/**
 * Questions > Preview Question: one YAML, in the Simulator, stored nowhere.
 *
 * What it is for: the loop between writing a Question and knowing it is right. Extraction
 * writes a file, and until now the only way to see it rendered was to upload it -- which
 * puts it in the Bank, gives it an id, and makes a file you were not sure about into a
 * Question somebody has to retire. This reads and validates a file through the same two
 * calls the upload makes, then opens it. Nothing is written.
 *
 * The drop zone and the box are ONE path, not two: a dropped file fills the box rather than
 * going straight to the API. That is what makes the loop work -- a file that came back
 * refused is on screen, editable, and re-submitted without leaving the page or finding the
 * file again. It is also the honest reading of what a preview is: the text you are looking
 * at is the text that gets sent.
 */
export function QuestionPreview({ api, exam }: { api: QuestionsApi; exam: string }) {
  const [yaml, setYaml] = useState('')
  const [name, setName] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)

  // The refusals are caught here rather than carried out through the hook: the hook exists
  // to open a tab against a browser's pop-up rules, and every other screen that uses it has
  // nothing to be refused over.
  const [refusals, setRefusals] = useState<TryOutcome['refusals']>([])

  const open = useOpenInSimulator(async (text: string) => {
    const outcome = await api.tryFile(text)
    setRefusals(outcome.refusals)
    return outcome.launch ?? null
  })

  const take = (list: FileList | null) => {
    const file = list?.[0]
    if (!file) return
    setName(file.name)
    void file.text().then(setYaml)
  }

  return (
    <>
      {/* Half and half, as on Questions Management: the heading names the screen and the way
          in sits beside it rather than under it, where it pushed the box off the fold. It
          stacks on a narrow screen, heading first.

          The drag handlers are on the wrapper rather than on the strip, so a file let go
          anywhere on the screen -- over the box included -- is read as a file rather than
          pasted in as its own name by the browser's default. */}
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          take(event.dataTransfer.files)
        }}
      >
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <h2 className="font-heading text-h2 font-bold text-heading">Preview Question</h2>

          <section
            aria-label="The Question file"
            className={`rounded-lg border-2 border-dashed p-4 ${over ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}
          >
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={quiet} onClick={() => picker.current?.click()}>
                <Upload aria-hidden size={16} /> Choose a file
              </button>
              <input
                ref={picker}
                type="file"
                accept=".yaml,.yml"
                className="sr-only"
                aria-label="A Question file to preview"
                onChange={(event) => take(event.target.files)}
              />

              <span className="text-muted">
                {name
                  ? `${name} — edit it below and preview again`
                  : 'or drop one .yaml file here, or paste it below'}
              </span>
            </div>
          </section>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className={loud}
            disabled={open.isPending || yaml.trim().length === 0}
            onClick={() => open.mutate(yaml)}
          >
            <Eye aria-hidden size={16} />
            {open.isPending ? 'Reading it...' : 'View in Simulator'}
          </button>
        </div>

        <label className="mt-4 flex flex-col gap-1">
          <span className="text-muted">The file</span>
          <textarea
            value={yaml}
            spellCheck={false}
            rows={36}
            placeholder={placeholder(exam)}
            onChange={(event) => setYaml(event.target.value)}
            className="w-full resize-y rounded-md border border-ink-100 bg-page px-3 py-2 font-mono text-sm"
          />
        </label>

        {open.isError && (
          <p role="alert" className="mt-3 text-red-700">
            {open.error.message}
          </p>
        )}

        {refusals.length > 0 && <Refused refusals={refusals} />}
      </div>
    </>
  )
}

/**
 * Why it was turned away, by the key it was turned away over -- the same list the upload
 * screen shows, because it is the same validator that produced it.
 */
function Refused({ refusals }: { refusals: TryOutcome['refusals'] }) {
  return (
    <div className="mt-4" role="alert">
      <p className="font-bold">
        {refusals.length === 1 ? 'One thing' : `${refusals.length} things`} to fix before this can be
        looked at.
      </p>
      <ul className="mt-2 text-red-700">
        {refusals.map((refusal, at) => (
          <li key={at}>
            <code>{refusal.field}</code> — {refusal.message}
          </li>
        ))}
      </ul>
    </div>
  )
}

const placeholder = (exam: string) => `type: generated
exam_code: ${exam}
stem: |
  <p>...</p>
options:
  A: '...'`

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-full bg-green-800 px-5 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
